import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getClientsRegistry, updateClientFields, getMyFavoriteIds, toggleFavorite, CHANNEL_LABELS, type RegistryClient } from '../../lib/refonteApi';
import { useAuth } from '../../components/AuthProvider';
import { SkeletonRows } from '../../components/refonte/Skeleton';
import EmptyState from '../../components/refonte/EmptyState';

const STATUTS = [{ v: 'actif', l: 'Actif' }, { v: 'en_pause', l: 'En pause' }, { v: 'termine', l: 'Terminé' }];

const fmtD = (d?: string | null) => (d ? new Date(d + 'T00:00:00').toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: '2-digit' }) : '—');
const daysBetween = (a: string, b: string) => Math.round((new Date(a).getTime() - new Date(b).getTime()) / 86400000);

// Registre clients — annuaire de tous les clients + infos clés. Point d'entrée du hub interne
// (CSM / manager / SDR) ; chaque ligne ouvre la fiche client complète.
const ClientsRegistryPage: React.FC = () => {
  const nav = useNavigate();
  const { profile } = useAuth();
  const canEdit = ['csm', 'admin', 'super_admin'].includes(profile?.role || '');
  const [rows, setRows] = useState<RegistryClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [filt, setFilt] = useState<'actifs' | 'inactifs' | 'termines' | 'tous'>('actifs');

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try { setRows(await getClientsRegistry()); } catch (e: any) { setErr(e.message); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Favoris (persistés par utilisateur) — étoile par ligne, remontés dans la sidebar.
  const [favs, setFavs] = useState<Set<string>>(new Set());
  useEffect(() => { if (profile?.user_id) getMyFavoriteIds(profile.user_id).then(ids => setFavs(new Set(ids))).catch(() => {}); }, [profile?.user_id]);
  const toggleFav = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (!profile?.user_id) return;
    const on = !favs.has(id);
    setFavs(s => { const n = new Set(s); on ? n.add(id) : n.delete(id); return n; });
    await toggleFavorite(profile.user_id, id, on).catch(() => {});
    window.dispatchEvent(new Event('favorites-changed'));
  };

  // Repère « en production » relatif au snapshot (dev figé) : dernière prospection < 30 j du max.
  const snapMax = useMemo(() => rows.reduce((m, r) => (r.lastProspection && r.lastProspection > m ? r.lastProspection : m), '2000-01-01'), [rows]);
  const prod = (r: RegistryClient) => (r.lastProspection ? (daysBetween(snapMax, r.lastProspection) <= 30 ? 'ok' : 'stale') : 'none');
  const changeStatut = async (e: React.MouseEvent | React.ChangeEvent, id: string, v: string) => {
    e.stopPropagation();
    setRows(rs => rs.map(r => (r.id === id ? { ...r, statut: v } : r)));
    await updateClientFields(id, { statut: v }).catch(() => {});
  };

  // Catégorie basée sur le statut du client (actif / en pause / terminé).
  const cat = (r: RegistryClient): 'actifs' | 'inactifs' | 'termines' => {
    const s = r.statut || 'actif';
    if (s === 'termine') return 'termines';
    if (s === 'actif') return 'actifs';
    return 'inactifs'; // en_pause, inactif
  };
  const counts = { actifs: rows.filter(r => cat(r) === 'actifs').length, inactifs: rows.filter(r => cat(r) === 'inactifs').length, termines: rows.filter(r => cat(r) === 'termines').length };
  const filtered = rows.filter(r =>
    (filt === 'tous' || cat(r) === filt) &&
    (!q || `${r.name} ${r.company_name ?? ''}`.toLowerCase().includes(q.toLowerCase())));

  return (
    <div>
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-1">
        <h1 className="text-[21px] font-bold tracking-tight">Registre clients</h1>
        <span className="font-mono text-[11px] text-faint">{rows.length} clients</span>
      </div>
      <p className="text-dim text-[13px] mb-4">Tous les clients et leurs informations. Clique une ligne pour la fiche complète (contrat, calibrage, RDV, résumés, santé).</p>

      {err && <EmptyState tone="error" title="Chargement impossible" hint={err} className="mb-4" />}

      <div className="bg-panel border border-line rounded-card p-5">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher un client…" className="rounded-md border border-line-2 bg-panel px-2.5 py-1.5 text-[13px] w-64" />
          {([['actifs', `Actifs (${counts.actifs})`], ['inactifs', `Inactifs (${counts.inactifs})`], ['termines', `Terminés (${counts.termines})`], ['tous', 'Tous']] as const).map(([v, l]) => (
            <button key={v} onClick={() => setFilt(v)} className={`text-[11.5px] font-semibold px-2.5 py-1 rounded-md border ${filt === v ? 'bg-ink text-white border-ink' : 'border-line-2 text-dim hover:border-ink'}`}>{l}</button>
          ))}
          <span className="ml-auto font-mono text-[11px] text-faint">{filtered.length} affichés</span>
        </div>

        {loading ? <SkeletonRows count={10} /> : filtered.length === 0 ? (
          <EmptyState title="Aucun client" hint="Aucun client ne correspond au filtre." />
        ) : (
          <div className="overflow-x-auto -mx-1">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-line-2 font-mono text-[9.5px] uppercase tracking-widest text-faint">
                  <th className="py-2 px-2 font-semibold">Client</th>
                  <th className="py-2 px-2 font-semibold">Offre</th>
                  <th className="py-2 px-2 font-semibold hidden sm:table-cell">Jours/mois</th>
                  <th className="py-2 px-2 font-semibold">Statut</th>
                  <th className="py-2 px-2 font-semibold">Calibrage</th>
                  <th className="py-2 px-2 font-semibold whitespace-nowrap hidden lg:table-cell" title="RDV bookés / honorés">RDV</th>
                  <th className="py-2 px-2 font-semibold whitespace-nowrap hidden lg:table-cell" title="Réponses mail · LinkedIn">Mail · LI</th>
                  <th className="py-2 px-2 font-semibold whitespace-nowrap hidden md:table-cell">Dern. prospection</th>
                  <th className="py-2 px-2 font-semibold">Santé</th>
                  <th className="py-2 px-2 w-6"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {filtered.map(r => (
                  <tr key={r.id} onClick={() => nav(`/app/clients/${r.id}`)} className="cursor-pointer hover:bg-panel-2 transition-colors">
                    <td className="py-2.5 px-2">
                      <div className="flex items-center gap-1.5">
                        <button onClick={e => toggleFav(e, r.id)} className={`text-[14px] leading-none ${favs.has(r.id) ? 'text-amber-400' : 'text-line-2 hover:text-amber-400'}`} title={favs.has(r.id) ? 'Retirer des favoris' : 'Ajouter aux favoris'}>{favs.has(r.id) ? '★' : '☆'}</button>
                        <div className="text-[13px] font-semibold">{r.name}</div>
                      </div>
                      {r.company_name && r.company_name !== r.name && <div className="text-[11px] text-faint ml-5">{r.company_name}</div>}
                    </td>
                    <td className="py-2.5 px-2">
                      {r.offers.length ? (
                        <span className="inline-flex flex-wrap gap-1">
                          {r.offers.map(o => <span key={o} className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-raise text-dim">{CHANNEL_LABELS[o] || o}</span>)}
                        </span>
                      ) : <span className="text-[12px] text-faint" title="Aucun calibrage actif">{r.contract_type || '—'}</span>}
                    </td>
                    <td className="py-2.5 px-2 font-mono text-[12px] text-dim hidden sm:table-cell">{r.days_per_month ?? '—'}</td>
                    <td className="py-2.5 px-2" onClick={e => e.stopPropagation()}>
                      {canEdit ? (
                        <select value={r.statut || 'actif'} onChange={e => changeStatut(e, r.id, e.target.value)} className="rounded-md border border-line-2 bg-panel px-1.5 py-1 text-[11.5px]">
                          {STATUTS.map(s => <option key={s.v} value={s.v}>{s.l}</option>)}
                        </select>
                      ) : <span className="text-[12px] text-dim">{STATUTS.find(s => s.v === (r.statut || 'actif'))?.l}</span>}
                    </td>
                    <td className="py-2.5 px-2">{r.calibrageDone ? <span className="font-mono text-[9px] px-1.5 py-0.5 rounded bg-st-ok/10 text-st-ok">✓ fait</span> : <span className="font-mono text-[9px] px-1.5 py-0.5 rounded bg-raise text-faint">à faire</span>}</td>
                    <td className="py-2.5 px-2 font-mono text-[11.5px] tabular-nums whitespace-nowrap hidden lg:table-cell">
                      {r.rdvBooked ? <span className="text-dim">{r.rdvBooked} <span className="text-faint">booké{r.rdvBooked > 1 ? 's' : ''}</span> · <span className="text-ink">{r.rdvHeld}</span> <span className="text-faint">hon.</span></span> : <span className="text-faint">—</span>}
                    </td>
                    <td className="py-2.5 px-2 font-mono text-[11.5px] tabular-nums whitespace-nowrap hidden lg:table-cell">
                      {(r.mailReplies || r.liReplies || r.mailCampaigns || r.liCampaigns)
                        ? <span className="text-dim">{r.mailReplies || 0}<span className="text-faint">✉</span> · {r.liReplies || 0}<span className="text-faint">in</span></span>
                        : <span className="text-faint">—</span>}
                    </td>
                    <td className="py-2.5 px-2 font-mono text-[11.5px] text-dim tabular-nums whitespace-nowrap hidden md:table-cell">{fmtD(r.lastProspection)}</td>
                    <td className="py-2.5 px-2">
                      <span className={`inline-flex items-center gap-1.5 font-mono text-[10px] ${prod(r) === 'ok' ? 'text-st-ok' : prod(r) === 'stale' ? 'text-st-warn' : 'text-faint'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${prod(r) === 'ok' ? 'bg-st-ok' : prod(r) === 'stale' ? 'bg-st-warn' : 'bg-line-2'}`} />
                        {prod(r) === 'ok' ? 'en prod' : prod(r) === 'stale' ? 'inactif' : '—'}
                      </span>
                    </td>
                    <td className="py-2.5 px-2 font-mono text-[11px] text-faint text-right">›</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientsRegistryPage;
